/**
 * Proof → voucher conversion at the completion step (registration-wallets
 * §4.3, §4.5; registration-restructure §7).
 *
 * Three legs reach "Confirming Your Registration", and only one of them has a
 * payment proof that must become a voucher ROW before the node can resolve the
 * lookup id the wizard is about to send to `register/begin`:
 *
 *   paid           Stripe popup, Stripe redirect, or the e2e test hook. The
 *                  client minted its voucher code BEFORE the card was charged
 *                  (the webhook creates the lock against the key that code
 *                  derives) and stashed it there and then — so the code's
 *                  presence in the stash says nothing about whether the row
 *                  exists yet. `POST /api/voucher/purchase` creates it.
 *   typed-voucher  A code the member already held. Its row exists; there is no
 *                  proof. The payment step stamps `registrationId` `voucher_…`.
 *   waived         `Payment:Mode=waived` (dev). No proof, and the node REFUSES
 *                  to convert in waived mode (W2), so nothing to do.
 *
 * The guard this replaces (`!voucherCode && !paymentWaived`) dated from when
 * the code came back FROM the conversion; after §4.3 it was always false on the
 * paid leg, and begin was sent a lookup id no row carried — "Unknown or invalid
 * voucher code" on every pay-and-continue registration (found live 2026-09-14).
 *
 * Pure: the wizards inject the network calls, and `proof-conversion.test.ts`
 * pins every branch.
 */

export type PaymentLeg = 'paid' | 'typed-voucher' | 'waived';

/** Prefix the payment step stamps on a typed voucher's synthetic registrationId. */
const TYPED_VOUCHER_PREFIX = 'voucher_';
/** Prefix of a waived registration's synthetic registrationId (survives a resume). */
const WAIVED_PREFIX = 'waived_';

/** Which leg settled the fee, from the stash's payment fields. */
export function paymentLegOf(shared: Record<string, unknown>, registrationId: string): PaymentLeg {
    if (shared['paymentWaived'] === true || registrationId.startsWith(WAIVED_PREFIX)) return 'waived';
    if (registrationId.startsWith(TYPED_VOUCHER_PREFIX)) return 'typed-voucher';
    return 'paid';
}

/**
 * True when the completion step must convert the proof before `begin`. The
 * `voucherConverted` flag is written to `shared` (and so to the stash, which
 * persists `shared` verbatim) right after a successful conversion, so a retry
 * or a resume goes straight to the reservation instead of re-sending a proof
 * that may since have expired.
 */
export function needsProofConversion(shared: Record<string, unknown>, registrationId: string): boolean {
    return paymentLegOf(shared, registrationId) === 'paid' && shared['voucherConverted'] !== true;
}

/**
 * Convert once, with the ROW as the fallback truth. Conversion is idempotent on
 * the Stripe session (a replayed proof reports the same batch, probe A7), so a
 * repeat is harmless — but a proof lives one hour. A crash between a successful
 * conversion and the stash's flag write, resumed after that hour, would fail
 * the purchase although the row exists; `rowExists` (the wizard wires
 * `POST /api/voucher/inspect` on the lookup id) settles it. A lookup that fails
 * counts as "no row", and the PURCHASE error is what surfaces.
 */
export async function convertProofOnce(opts: {
    purchase: () => Promise<unknown>;
    rowExists: () => Promise<boolean>;
}): Promise<'converted' | 'already-present'> {
    try {
        await opts.purchase();
        return 'converted';
    } catch (err) {
        let present = false;
        try {
            present = await opts.rowExists();
        } catch {
            present = false;
        }
        if (present) return 'already-present';
        throw err;
    }
}
